import { NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DsBadgeComponent } from '@design-system/ds-badge/component/ds-badge.component';
import { DsButtonComponent } from '@design-system/ds-button/component/ds-button.component';
import { DsTabGroupComponent } from '@design-system/ds-tab-group/component/ds-tab-group.component';
import { DsTextComponent } from '@design-system/ds-text/component/ds-text.component';
import { ServiceRequestFlagTabEnum } from '@modals/service-request-flags/enums/service-request-flag-tab.enum';
import { ServiceRequestFlag } from '@modals/service-request-flags/interfaces/service-request-flag.interface';
import { ServiceRequestFlagsModalDataInterface } from '@modals/service-request-flags/interfaces/service-request-flags-modal-data.interface';
import { SERVICE_REQUEST_FLAGS_MAX_COUNT } from '@modals/service-request-flags/properties/service-request-flag.properties';
import { ActiveFlagsTableComponent } from '../modules/active-flags-table/component/active-flags-table.component';
import { ResolvedFlagsTableComponent } from '../modules/resolved-flags-table/component/resolved-flags-table.component';

@Component({
  selector: 'app-flag-list',
  templateUrl: './flag-list.component.html',
  styleUrls: ['./flag-list.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    MatTooltipModule,
    DsBadgeComponent,
    DsButtonComponent,
    DsTabGroupComponent,
    DsTextComponent,
    ActiveFlagsTableComponent,
    ResolvedFlagsTableComponent,
  ],
})
export class FlagListComponent implements OnChanges {
  @Input() data: ServiceRequestFlagsModalDataInterface;
  @Input() activeFlags: ServiceRequestFlag[] = [];
  @Input() resolvedFlags: ServiceRequestFlag[] = [];
  @Input() activeTab: ServiceRequestFlagTabEnum = ServiceRequestFlagTabEnum.Active;

  @Output() readonly activeTabChange = new EventEmitter<ServiceRequestFlagTabEnum>();
  @Output() readonly createFlag = new EventEmitter<void>();
  @Output() readonly editFlag = new EventEmitter<ServiceRequestFlag>();
  @Output() readonly resolveFlag = new EventEmitter<ServiceRequestFlag>();

  readonly flagTypes = ServiceRequestFlagTabEnum;
  private readonly tabTypes: ServiceRequestFlagTabEnum[] = [ServiceRequestFlagTabEnum.Active, ServiceRequestFlagTabEnum.Resolved];
  tabLabels: string[] = [];
  selectedTabLabel: string | null = null;
  overdueCount = 0;
  flagLimitLabel = '';
  isMaxFlagLimit = false;

  ngOnChanges(): void {
    this.tabLabels = [
      `${ServiceRequestFlagTabEnum.Active} (${this.activeFlags.length})`,
      `${ServiceRequestFlagTabEnum.Resolved} (${this.resolvedFlags.length})`,
    ];
    this.selectedTabLabel = this.tabLabels[this.tabTypes.indexOf(this.activeTab)];

    const flagCount = this.activeFlags.length + this.resolvedFlags.length;
    this.isMaxFlagLimit = flagCount >= SERVICE_REQUEST_FLAGS_MAX_COUNT;
    this.flagLimitLabel = `${flagCount} of ${SERVICE_REQUEST_FLAGS_MAX_COUNT} flags`;
    // A flag without a follow up time has a 1970 date, so it is "passed due" but not overdue.
    this.overdueCount = this.activeFlags.filter((flag) => flag.followUpTime && flag.isPassedDue).length;
  }

  /**
   * Shows the flags of the selected tab.
   * @param label - The label of the selected tab, with the flag count.
   */
  onTabChange(label: string): void {
    this.selectedTabLabel = label;
    this.activeTabChange.emit(this.tabTypes[this.tabLabels.indexOf(label)] ?? ServiceRequestFlagTabEnum.Active);
  }
}
